import { callAuthorizedGraphQLEndpoint } from "../module/graphQL";
import { authorization } from "../module/auth";
import {
  DEFAULT_SESSION_KEY,
  LIST_CLIENT_CONVERSATIONS,
  buildCreateConversationVariables,
  selectCreateConversationDocument,
  sessionCacheKey,
} from "./conversationApi.mjs";
import { getConversationId, setConversationId, subscribeConversationId } from "./conversationIdStore.mjs";

export { getConversationId, subscribeConversationId };

// The backend (served by oppizi-api) owns the whole handshake: it provisions the
// TalkJS user, creates/reuses the client-oads conversation, stamps every custom
// field, and returns a signed session token. The Copilot library takes it from
// there — this module only fetches it. The documents and the pure builders live
// in ./conversationApi.mjs (dependency-free, executed by node:test).

/**
 * Create-or-reuse a client conversation and get a signed TalkJS session for it.
 *
 * @param {{ conversationId?: string, fresh?: boolean }} [options]
 *   no options → the account's default conversation;
 *   `conversationId` → reopen that owned thread (foreign/unknown rejects with
 *   "Conversation not found"); `fresh: true` → mint a new thread.
 * @returns {Promise<{conversationId: string, alreadyExists: boolean, token: {appId: string, userId: string, signature: string}}>}
 */
export const createClientConversation = async (options) => {
  // 2-arg call: campaign-builder's callAuthorizedGraphQLEndpoint takes the
  // endpoint URL as its (defaulted) 3rd arg, not an options object. It rejects on
  // GraphQL/auth errors upstream, so here we only guard the HTTP + data shape.
  // The argument-less document for the default thread, the extended one only
  // when a thread is reopened/minted — production oppizi-api may not expose
  // the arguments yet (see conversationApi.mjs).
  const variables = buildCreateConversationVariables(options);
  const response = await callAuthorizedGraphQLEndpoint(selectCreateConversationDocument(variables), variables);
  if (!response.ok) throw new Error(`createClientConversation: HTTP ${response.status}`);
  const result = await response.json();
  if (!result.data?.talkJsCreateClientConversation)
    throw new Error("createClientConversation: backend returned no data");
  return result.data.talkJsCreateClientConversation;
};

/**
 * The user's conversations, most recently active first — already in the
 * library's CopilotConversationSummary shape (epoch-ms timestamps). Not cached:
 * the History view calls it on every open, and the list is what just changed.
 *
 * @returns {Promise<Array<{conversationId: string, title: string | null, summary: string | null, createdAt: number, lastMessageAt: number | null, isDefault: boolean}>>}
 */
export const listClientConversations = async () => {
  const response = await callAuthorizedGraphQLEndpoint(LIST_CLIENT_CONVERSATIONS, {});
  if (!response.ok) throw new Error(`listClientConversations: HTTP ${response.status}`);
  const result = await response.json();
  const list = result.data?.talkJsListClientConversations;
  if (!Array.isArray(list)) throw new Error("listClientConversations: backend returned no data");
  return list;
};

// Cache the in-flight/resolved conversation PER user id AND per conversation, so
// an in-session account switch (without a full reload) refetches instead of
// handing client B client A's cached signed token, and switching back to a
// thread already resolved this session costs no round-trip. A rejected fetch
// clears that entry so it isn't permanently poisoned. (Keyed map, not
// react-query — keeps the single consumer light.)
const sessionByKey = new Map();

/**
 * File a conversation `createClientConversation` returned under its id, so the
 * `sessionKey` switch that follows a `fresh: true` mint resolves from cache
 * instead of asking the backend to reopen the thread it just created.
 */
export const primeClientSession = (conversation) => {
  const userId = authorization.getUser()?.id;
  if (!userId || !conversation?.conversationId) return;
  sessionByKey.set(sessionCacheKey(userId, conversation.conversationId), Promise.resolve(conversation));
};

/**
 * The library asks for the session itself (`fetchSession`) instead of taking
 * pre-resolved token props, so hand it the handshake in the flat shape it wants.
 * With `lazyLoad` the library calls this at most once per mount and once per
 * `sessionKey` change; the cache makes the latter free for a thread seen before.
 *
 * @param {string} [conversationId] the thread to resolve; omitted/'default' →
 *   the account's default conversation.
 * @returns {Promise<{userId: string, appId: string, signature: string, conversationId: string}>}
 */
export const fetchClientSession = async (conversationId) => {
  const userId = authorization.getUser()?.id;
  const key = userId ? sessionCacheKey(userId, conversationId) : null;
  let promise = key ? sessionByKey.get(key) : undefined;
  if (!promise) {
    const wanted = conversationId && conversationId !== DEFAULT_SESSION_KEY ? conversationId : undefined;
    promise = createClientConversation(wanted ? { conversationId: wanted } : undefined).catch((error) => {
      if (key) sessionByKey.delete(key);
      throw error;
    });
    if (key) sessionByKey.set(key, promise);
  }
  const conversation = await promise;
  // Also file it under the id it actually resolved to (the default thread's
  // real id, or the reopened one): picking that thread in History later, or
  // switching to a thread `onNew` just minted (see primeClientSession), then
  // costs no second handshake.
  if (userId) sessionByKey.set(sessionCacheKey(userId, conversation.conversationId), promise);
  // The control channel is keyed on the conversation: publish the id as it
  // goes past (conversationIdStore) rather than resolving the handshake twice.
  setConversationId(conversation.conversationId);
  return {
    userId: conversation.token.userId,
    appId: conversation.token.appId,
    signature: conversation.token.signature,
    conversationId: conversation.conversationId,
  };
};
